import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import { Box, Menu, MenuItem } from "@mui/material";
import { useCallback, useContext, useEffect } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { ampli } from "@/config";
import { ActionContext } from "@/models/ActionsProvider";
import type { DriverRow } from "@/types/driver";

interface Props {
  anchorPosition: { top: number; left: number } | undefined;
  drivers: DriverRow[];
  onClose: () => void;
}

/** Matches which items `DriverContextMenu` renders; use to avoid opening an empty menu. */
export function getDriverContextMenuVisibility(drivers: DriverRow[]) {
  return {
    showMarkAsRead: drivers.some((d) => d.unread),
  };
}

export function driversHaveContextMenuActions(drivers: DriverRow[]): boolean {
  return getDriverContextMenuVisibility(drivers).showMarkAsRead;
}

function markAsReadLabel(drivers: DriverRow[]): string {
  if (drivers.length > 1) {
    return `Mark ${drivers.length} drivers as read`;
  }
  const name = drivers[0].name ?? "this driver";
  return `Mark ${name} as read`;
}

export default function DriverContextMenu({ anchorPosition, drivers, onClose }: Props) {
  const { accountEvent } = useContext(ActionContext);

  // A plain boolean rather than a memo: the object is rebuilt every render either way, and the effect below
  // depends on the flag, not the object.
  const { showMarkAsRead } = getDriverContextMenuVisibility(drivers);

  useEffect(() => {
    if (anchorPosition != null && drivers.length > 0 && !showMarkAsRead) {
      onClose();
    }
  }, [anchorPosition, drivers.length, showMarkAsRead, onClose]);

  const handleMarkAsRead = useCallback(() => {
    if (!accountEvent) return;
    for (const driver of drivers) {
      if (driver.unread && driver.feedId != null) {
        accountEvent("Marked Feed as Read", { feedId: driver.feedId });
        if (driver.accountId) {
          ampli.markDriverAsRead({ accountGuid: driver.accountId });
        }
      }
    }
    onClose();
  }, [drivers, accountEvent, onClose]);

  if (drivers.length === 0 || !showMarkAsRead) return null;

  return (
    <Menu
      open={anchorPosition != null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
    >
      <MenuItem
        data-sb={TestIds.driverView.contextMenuMarkRead}
        onClick={handleMarkAsRead}
        sx={{ display: "flex", gap: 1, alignItems: "center" }}
      >
        <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
          <DraftsOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>{markAsReadLabel(drivers)}</Box>
      </MenuItem>
    </Menu>
  );
}
